import type { ReactElement } from 'react';
import { useController, type Control, type FieldValues, type Path } from 'react-hook-form';
import type { FieldDefinition, FieldValue } from '@gr/core/content/entities/fields';
import { useFormMessage } from '../../../../ui-kit/form/form';
import type { ReferenceLookup } from '../model/reference-search';
import { FieldValuesInput } from './FieldValuesInput';

export interface FormFieldValuesProps<T extends FieldValues> {
  control: Control<T>;
  /** Путь объекта значений в форме: values (запись) или customValues (пост). */
  name: Path<T>;
  fields: readonly FieldDefinition[];
  references: ReferenceLookup;
  disabled?: boolean;
}

/**
 * Значения пользовательских полей, привязанные к форме одним контроллером на весь объект.
 * Ошибки сервера приходят по путям «<name>.<ключ>» (applyServerErrors) и встают к своим полям.
 */
export function FormFieldValues<T extends FieldValues>({ control, name, fields, references, disabled }: FormFieldValuesProps<T>): ReactElement {
  const { field, fieldState } = useController({ control, name });
  const message = useFormMessage();
  const values = (field.value ?? {}) as Record<string, FieldValue>;
  const nested = (fieldState.error ?? {}) as Record<string, { message?: unknown } | undefined>;
  const errors = Object.fromEntries(fields.map((f) => [f.key, message(nested[f.key])]));
  return <FieldValuesInput fields={fields} values={values} errors={errors} references={references} disabled={disabled} onChange={(key, value) => field.onChange({ ...values, [key]: value })} />;
}
